import { useEffect, useRef, useState, useCallback } from 'react';

interface DiscordRPCState {
  connected: boolean;
  connecting: boolean;
  error: string | null;
}


interface TrackInfo {
  track: string;
  artist: string;
  image: string;
  duration: number;
  currentTime: number;
  isPaused: boolean;
  genre?: string;
  userId?: string;
  username?: string;
}

export const useDiscordRPC = () => {
  const [state, setState] = useState<DiscordRPCState>({
    connected: false,
    connecting: false,
    error: null
  });

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const enabled = useRef(false);
  // Only auto-reconnect if we previously had a successful connection
  const wasConnectedRef = useRef(false);
  // Limit reconnection attempts
  const reconnectAttemptsRef = useRef(0);
  const MAX_RECONNECT_ATTEMPTS = 3;

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN || wsRef.current?.readyState === WebSocket.CONNECTING) {
      return;
    }

    setState(prev => ({ ...prev, connecting: true, error: null }));

    try {
      const ws = new WebSocket('ws://127.0.0.1:9685');
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('✅ Discord companion connected');
        setState({ connected: true, connecting: false, error: null });
        // Mark that we've had a successful connection
        wasConnectedRef.current = true;
        // Reset reconnect attempts on successful connection
        reconnectAttemptsRef.current = 0;

        // Start heartbeat
        if (heartbeatIntervalRef.current) clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, 30000); // Ping every 30 seconds
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'status' || data.type === 'pong') {
            setState(prev => ({
              ...prev,
              connected: data.connected,
              connecting: false,
              error: data.connected ? null : 'Discord not connected'
            }));
          }
        } catch (err) {
          console.error('Error parsing message:', err);
        }
      };

      ws.onerror = () => {
        // Don't log error spam when companion isn't running
        if (wasConnectedRef.current) {
          console.log('Discord companion connection error');
        }
        setState({ connected: false, connecting: false, error: 'Connection error' });
      };

      ws.onclose = () => {
        // Only log if we were previously connected (not on initial failed attempts)
        if (wasConnectedRef.current) {
          console.log('🔌 Discord companion disconnected');
        }
        setState({ connected: false, connecting: false, error: 'Companion app not running' });

        // Clear heartbeat
        if (heartbeatIntervalRef.current) {
          clearInterval(heartbeatIntervalRef.current);
          heartbeatIntervalRef.current = null;
        }

        // Auto-reconnect ONLY if:
        // 1. User has enabled the connection
        // 2. We previously had a successful connection (companion was running)
        // 3. We haven't exceeded max reconnect attempts
        if (enabled.current && wasConnectedRef.current && reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
          reconnectAttemptsRef.current++;
          console.log(`🔄 Attempting to reconnect (${reconnectAttemptsRef.current}/${MAX_RECONNECT_ATTEMPTS})...`);
          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, 5000);
        } else if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
          console.log('⏹️ Stopped reconnecting after max attempts. Click to reconnect manually.');
          // Reset for next manual connection attempt
          wasConnectedRef.current = false;
          reconnectAttemptsRef.current = 0;
        }
      };
    } catch (err) {
      console.error('Failed to create WebSocket:', err);
      setState({ connected: false, connecting: false, error: 'Failed to connect' });
    }
  }, []);

  const disconnect = useCallback(() => {
    enabled.current = false;
    wasConnectedRef.current = false;
    reconnectAttemptsRef.current = 0;

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }

    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    setState({ connected: false, connecting: false, error: null });
  }, []);

  const updatePresence = useCallback((trackInfo: TrackInfo | null) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      return;
    }

    try {
      wsRef.current.send(JSON.stringify({
        type: 'update',
        siteUrl: window.location.origin, // Send current site URL
        ...trackInfo
      }));
    } catch (err) {
      console.error('Error updating Discord presence:', err);
    }
  }, []);

  const toggleConnection = useCallback(() => {
    if (state.connected || state.connecting) {
      disconnect();
    } else {
      enabled.current = true;
      reconnectAttemptsRef.current = 0; // Reset attempts for fresh connection
      connect();
    }
  }, [state.connected, state.connecting, connect, disconnect]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  return {
    ...state,
    connect: () => {
      enabled.current = true;
      reconnectAttemptsRef.current = 0; // Reset attempts for fresh connection
      connect();
    },
    disconnect,
    toggleConnection,
    updatePresence
  };
};
